import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import { useGeo } from '../contexts/GeoContext';
import { toast } from 'sonner';
import { thumb as optThumb } from '../lib/imageUrl';

export default function WishlistPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wishlist, loading, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { formatPrice } = useGeo();

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/wishlist' } } });
    }
  }, [user, navigate]);

  const handleRemove = async (productId) => {
    const result = await removeFromWishlist(productId);
    if (result.success) {
      toast.success('Removed from wishlist');
    }
  };

  const handleAddToCart = async (product) => {
    const result = await addToCart(
      product.id,
      1,
      product.sizes?.[0] || null,
      product.colors?.[0] || null
    );
    if (result.success) {
      toast.success('Added to cart!');
    } else {
      toast.error(result.error);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#525252] hover:text-[#0A0A0A]">Home</Link>
            <span className="text-[#525252]">/</span>
            <span className="font-semibold">Wishlist</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-4xl font-black tracking-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
            MY WISHLIST
          </h1>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-[3/4] border-2 border-[#0A0A0A] skeleton"></div>
              ))}
            </div>
          ) : wishlist.items?.length === 0 ? (
            <div className="text-center py-16 border-2 border-[#0A0A0A] bg-white">
              <Heart className="w-16 h-16 mx-auto mb-4 text-[#525252]" />
              <p className="text-xl font-semibold mb-2">Your wishlist is empty</p>
              <p className="text-[#525252] mb-6">Save items you love to your wishlist</p>
              <Link
                to="/products"
                className="inline-block bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] px-8 py-3 font-semibold hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
              >
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {wishlist.items?.map((item, idx) => {
                const product = item.product;
                if (!product) return null;

                const discountedPrice = product.is_promo && product.promo_percentage
                  ? product.price * (1 - product.promo_percentage / 100)
                  : null;

                return (
                  <motion.div
                    key={item.product_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    className="border-2 border-[#0A0A0A] bg-white"
                    data-testid={`wishlist-item-${item.product_id}`}
                  >
                    {/* Image */}
                    <Link to={`/product/${product.id}`} className="block">
                      <div className="relative aspect-[3/4] bg-[#F5F5F5] border-b-2 border-[#0A0A0A] overflow-hidden">
                        {product.images?.[0] ? (
                          <img
                            src={optThumb(product.images[0])}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#525252]">
                            No Image
                          </div>
                        )}
                        {product.is_promo && product.promo_percentage && (
                          <span className="badge-promo absolute top-3 left-3">
                            -{product.promo_percentage}%
                          </span>
                        )}
                      </div>
                    </Link>

                    {/* Info */}
                    <div className="p-4">
                      <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">
                        {product.category}
                      </p>
                      <Link to={`/product/${product.id}`}>
                        <h3 className="font-semibold text-sm mb-2 hover:underline truncate">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-2 mb-4">
                        {discountedPrice ? (
                          <>
                            <span className="font-bold price-sale">
                              {formatPrice(discountedPrice)}
                            </span>
                            <span className="price-original text-sm">
                              {formatPrice(product.price)}
                            </span>
                          </>
                        ) : (
                          <span className="font-bold">
                            {formatPrice(product.price)}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleAddToCart(product)}
                          data-testid={`wishlist-add-to-cart-${item.product_id}`}
                          className="flex-1 bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none text-xs h-10 font-semibold hover:translate-y-[-1px] hover:shadow-[2px_2px_0px_rgba(0,0,0,0.3)] transition-all"
                        >
                          <ShoppingBag className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                        <Button
                          onClick={() => handleRemove(item.product_id)}
                          variant="outline"
                          data-testid={`wishlist-remove-${item.product_id}`}
                          className="w-10 h-10 p-0 border-2 border-[#0A0A0A] rounded-none hover:bg-[#FAD4D4] transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
